import React from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import { Language } from '../../App';

interface TeamMember {
  name: string;
  role: {
    en: string;
    hi: string;
  };
  phone: string;
  image: string;
  description: {
    en: string;
    hi: string;
  };
}

interface Translations {
  title: string;
  subtitle: string;
  description: string;
  contactButton: string;
  achievementsTitle: string;
  contactFounder: string;
  successStoriesTitle: string;
  successStoriesSubtitle: string;
  viewMore: string;
}

const translations: Record<Language, Translations> = {
  en: {
    title: "About Us",
    subtitle: "Our Team",
    description: "Meet the dedicated individuals who work tirelessly to serve our community and uphold our values of selfless service and spiritual growth.",
    contactButton: "Contact Us",
    achievementsTitle: "Key Achievements",
    contactFounder: "Contact Founder",
    successStoriesTitle: "Journeys of Transformation",
    successStoriesSubtitle: "Real Stories of Hope and Change",
    viewMore: "Read Full Story"
  },
  hi: {
    title: "हमारे बारे में",
    subtitle: "हमारी टीम",
    description: "हमारी टीम के समर्पित सदस्यों से मिलें जो निस्वार्थ सेवा और आध्यात्मिक विकास के हमारे मूल्यों को बनाए रखने के लिए अथक प्रयास करते हैं।",
    contactButton: "संपर्क करें",
    achievementsTitle: "प्रमुख उपलब्धियां",
    contactFounder: "संस्थापक से संपर्क करें",
    successStoriesTitle: "परिवर्तन की यात्राएं",
    successStoriesSubtitle: "आशा और बदलाव की वास्तविक कहानियां",
    viewMore: "पूरी कहानी पढ़ें"
  }
};

const teamMembers: TeamMember[] = [
  {
    name: "Shri Radhe Krishna",
    role: {
      en: "Founder & Spiritual Guide",
      hi: "संस्थापक और आध्यात्मिक मार्गदर्शक"
    },
    phone: "+91 95409 36061",
    image: "/images/manojkumar_photo.jpeg",
    description: {
      en: "With over 25 years of spiritual leadership and community service, Shri Radhe Krishna has dedicated his life to uplifting society through spiritual wisdom and selfless service. His vision combines ancient spiritual teachings with modern social welfare, creating a unique approach to community development. Under his guidance, the organization has touched thousands of lives through various initiatives in education, healthcare, and spiritual development. His teachings emphasize the importance of selfless service (seva) and spiritual growth as the foundation for a harmonious society.",
      hi: "25 वर्षों से अधिक के आध्यात्मिक नेतृत्व और समाज सेवा के साथ, श्री राधे कृष्ण ने अपना जीवन आध्यात्मिक ज्ञान और निस्वार्थ सेवा के माध्यम से समाज को उत्थान के लिए समर्पित किया है। उनकी दृष्टि प्राचीन आध्यात्मिक शिक्षाओं को आधुनिक सामाजिक कल्याण के साथ जोड़ती है, जिससे समुदाय विकास का एक अनूठा दृष्टिकोण बनता है। उनके मार्गदर्शन में, संगठन ने शिक्षा, स्वास्थ्य सेवा और आध्यात्मिक विकास के विभिन्न कार्यक्रमों के माध्यम से हजारों जीवनों को छुआ है। उनकी शिक्षाएं एक सामंजस्यपूर्ण समाज की नींव के रूप में निस्वार्थ सेवा (सेवा) और आध्यात्मिक विकास के महत्व पर जोर देती हैं।"
    }
  },
  {
    name: "Mr. Shobhit Singh",
    role: {
      en: "Chairman & President",
      hi: "अध्यक्ष और राष्ट्रपति"
    },
    phone: "+91 80105 01344",
    image: "/images/shobhitsingh_photo.jpeg",
    description: {
      en: "The head of the organization who leads meetings and oversees all activities, ensuring the smooth functioning of all initiatives and maintaining the organization's vision and mission.",
      hi: "संगठन के प्रमुख जो बैठकों का नेतृत्व करते हैं और सभी गतिविधियों की देखरेख करते हैं, सभी पहलों के सुचारू संचालन को सुनिश्चित करते हैं और संगठन की दृष्टि और मिशन को बनाए रखते हैं।"
    }
  },
  {
    name: "Smt. Neha Singh",
    role: {
      en: "Treasurer",
      hi: "कोषाध्यक्ष"
    },
    phone: "+91 95409 36061",
    image: "/images/neha_photo.jpeg",
    description: {
      en: "Responsible for managing the organization's finances and maintaining accounts, ensuring transparency and proper utilization of funds for various social welfare programs.",
      hi: "संगठन के वित्त का प्रबंधन और खातों का रखरखाव करने के लिए जिम्मेदार, विभिन्न सामाजिक कल्याण कार्यक्रमों के लिए धन के पारदर्शी और उचित उपयोग को सुनिश्चित करते हैं।"
    }
  },
  {
    name: "Mr. Rajkumar Singh",
    role: {
      en: "Auditor",
      hi: "लेखा परीक्षक"
    },
    phone: "+91 80105 01344",
    image: "/images/rajkumarsingh_photo.jpeg",
    description: {
      en: "Reviews and verifies the financial records to ensure accuracy and transparency, maintaining the highest standards of financial integrity in all organizational activities.",
      hi: "सटीकता और पारदर्शिता सुनिश्चित करने के लिए वित्तीय रिकॉर्ड की समीक्षा और सत्यापन करता है, सभी संगठनात्मक गतिविधियों में वित्तीय अखंडता के उच्चतम मानकों को बनाए रखता है।"
    }
  }
];

const AboutUs: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const founder = teamMembers[0];
  const otherMembers = teamMembers.slice(1);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Founder Section */}
        <div className="mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-red-800 mb-4">{t.title}</h1>
              <p className="text-xl text-gray-600">{t.description}</p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="p-8">
                <div className="flex flex-col items-center">
                  <div className="relative w-64 h-64 mb-8">
                    <div className="absolute inset-0 rounded-full overflow-hidden">
                      <img
                        src={founder.image}
                        alt={founder.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">{founder.name}</h2>
                  <p className="text-xl text-red-700 mb-4">{founder.role[language]}</p>
                  <p className="text-gray-600 text-lg mb-6 text-center max-w-2xl">
                    {founder.description[language]}
                  </p>
                  
                  <a
                    href={`tel:${founder.phone}`}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-700 hover:bg-red-800 transition-colors duration-300"
                  >
                    {t.contactFounder}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Other Team Members Section */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-red-800 text-center mb-12">{t.subtitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {otherMembers.map((member, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-6">
                  <div className="flex flex-col items-center">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-48 h-48 rounded-full object-cover mb-4"
                    />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{member.name}</h3>
                    <p className="text-red-700 mb-4">{member.role[language]}</p>
                    <p className="text-gray-600 text-center mb-4">{member.description[language]}</p>
                    <a
                      href={`tel:${member.phone}`}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-700 hover:bg-red-800 transition-colors duration-300"
                    >
                      {t.contactButton}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        
      </div>
    </div>
  );
};

export default AboutUs; 